import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Table, Row, Col, Button, Modal, message, Space, Input, Select } from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from 'app/shared/api';
import {
  filterData,
  getPackageProductById,
  listPackageProductsData,
  resetPackageModal,
  setFormErrors
} from 'app/pages/Products/actions';
import { PackageProduct } from 'app/shared/model/Package';
import { FilterValueType, Product, ProductFilter, ProductKey } from 'app/shared/model/ProductBase';
import DateHelper from 'app/helpers/dateHelper';
import filterHelper from 'app/helpers/filterHelper';
import TablePagination from 'app/shared/components/Table/TablePagination';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import { getFilteredData, getPackageProductsData } from '../selectors';
import AddEditPackageForm from './AddEditPackageForm';
import '../Page.css';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';
import { getDivisionId } from 'app/shared/division/getDivisionId';

export interface PackagesGridProps {}

interface StateProps {
  packageProducts?: PackageProduct[];
  filteredData?: PackageProduct[];
}

interface DispatchProps {
  onFilterData: (filteredData: PackageProduct[] | undefined) => void;
  onPackagesData: (packages: PackageProduct[]) => void;
  onSelectedPackageData: (packageProduct: PackageProduct | undefined) => void;
  onOpenNewPackage: () => void;
}

type InternalProps = PackagesGridProps & StateProps & DispatchProps;

const PackagesGrid: React.FC<InternalProps> = (props) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState<ProductFilter[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [searchFilteredData, setSearchFilteredData] = React.useState<PackageProduct[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalPackageId, setModalPackageId] = React.useState<number | null>(null);
  const [loadingPackage, setLoadingPackage] = React.useState(false);

  const packageList = props.packageProducts ?? [];
  const { onPackagesData, onFilterData } = props;

  const publishFilteredData = React.useCallback((
    nextFilters = filters,
    nextShowFilters = showFilters,
    nextStatus = statusFilter,
    nextSearchActive = isSearchActive,
    nextSearchData = searchFilteredData,
  ) => {
    onFilterData(buildGridFilteredList(packageList, {
      showFilters: nextShowFilters,
      filters: nextFilters,
      isSearchActive: nextSearchActive,
      searchFilteredData: nextSearchData,
      statusFilter: nextStatus,
    }));
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, packageList, onFilterData]);

  const handleFilterChange = (
    value: any,
    field: keyof ProductKey,
    valueType: FilterValueType = FilterValueType.primaryValue
  ) => {
    const newFilters = filterHelper.getFilters(filters, value, field, valueType);
    setFilters(newFilters);
    publishFilteredData(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters([]);
    setCurrentPage(1);
    publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
  };

  const getUniqueMinimumWeeksFilter = (list: PackageProduct[]) => {
    const weeks = new Set<number>();
    list.forEach((item) => {
      if (item.minimumWeeks != null) {
        weeks.add(item.minimumWeeks);
      }
    });
    return Array.from(weeks)
      .sort((a, b) => a - b)
      .map((week) => ({ label: String(week), value: week }));
  };

  const applyPackageResults = React.useCallback((list: PackageProduct[]) => {
    onPackagesData(list);
    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
  }, [onPackagesData, publishFilteredData, filters, showFilters, statusFilter, isSearchActive, searchFilteredData]);

  const filteredList = (props.filteredData ?? packageList) as PackageProduct[];
  const sortedList = React.useMemo(() => {
    return sortProductsWithStatus(filteredList, sortKey, sortOrder, statusFilter);
  }, [filteredList, sortKey, sortOrder, statusFilter]);
  const pagedData = TablePagination.dataPaging(sortedList, currentPage, itemsPerPage) as PackageProduct[];
  const totalItems = sortedList.length;

  const refreshPackages = React.useCallback(async () => {
    const divisionId = getDivisionId();
    if (!divisionId) {
      return [];
    }
    const data = await api.getPackageProducts(divisionId);
    applyPackageResults(data);
    return data;
  }, [applyPackageResults]);

  const refreshGridEvent = React.useCallback(async () => {
    await refreshPackages();
  }, [refreshPackages]);

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.PackageEventPublished],
    refreshGridEvent,
    getDivisionId()
  );

  React.useEffect(() => {
    const maxPages = Math.ceil(Math.max(totalItems, 1) / itemsPerPage);
    if (currentPage > maxPages) {
      setCurrentPage(1);
    }
  }, [totalItems, itemsPerPage, currentPage]);

  const handlePackageOpen = async (id: number) => {
    try {
      setLoadingPackage(true);
      const pkg = await api.getPackageById(id);
      if (pkg) {
        props.onSelectedPackageData(pkg);
        setModalPackageId(id);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to load package details', error);
      message.error('Failed to open package. Please try again.');
    } finally {
      setLoadingPackage(false);
    }
  };

  useAutoOpenProductModal('Packages', (id) => {
    void handlePackageOpen(id);
  });

  const handleAddNew = () => {
    props.onOpenNewPackage();
    setModalPackageId(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalPackageId(null);
  };

  const handlePackageSaved = React.useCallback(async (latest?: PackageProduct[]) => {
    try {
      if (latest && latest.length) {
        applyPackageResults(latest);
      } else {
        await refreshPackages();
      }
    } catch (error) {
      console.error('Failed to refresh packages after save', error);
      message.warning('Package saved, but the list could not be refreshed automatically.');
    } finally {
      setCurrentPage(1);
      handleModalClose();
    }
  }, [applyPackageResults, refreshPackages]);

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number, record: PackageProduct) => (
        <>
          {id}
          {record.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
        </>
      ),
    },
    {
      title: 'Package Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: PackageProduct) =>
        record.entityUpdate ? (
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
        ) : (
          <a
            href="#0"
            onClick={e => {
              e.preventDefault();
              handlePackageOpen(Number(record.id));
            }}
          >
            {text}
          </a>
        ),
    },
    {
      title: 'Minimum weeks',
      dataIndex: 'minimumWeeks',
      key: 'minimumWeeks',
      width: 140,
      render: (v: number | null | undefined) => (v ?? '-'),
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 160,
      ...getColumnSortProps('lastModifiedDate'),
      render: (value: Date | string | undefined) => DateHelper.ToDateString(value, DateHelper.DDMMMYYYY),
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 160,
      ...getColumnSortProps('lastUpdatedBy'),
      render: (value: string | undefined) => value ?? '',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      align: 'center' as const,
      render: (_: boolean, record: PackageProduct) =>
        record.active
          ? <span style={{ color: 'green' }}>Yes</span>
          : <span style={{ color: 'red' }}>No</span>,
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={packageList as unknown as Product[]}
                onSearch={(data, searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(data as unknown as PackageProduct[]);
                  publishFilteredData(filters, showFilters, statusFilter, searchActive, data as unknown as PackageProduct[]);
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
              }}
            />
          </div>
        </Col>
        <Col flex={3} style={{ textAlign: 'right' }}>
          <Space>
            <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(filteredList) && filteredList.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
            <TableActionButtons
              showFilters={showFilters}
              dataList={filteredList as unknown as Product[]}
              onShowFiltersClick={() => {
                const nextShowFilters = !showFilters;
                setShowFilters(nextShowFilters);
                if (nextShowFilters) {
                  setFilters([]);
                  publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                } else {
                  publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                }
                setCurrentPage(1);
              }}
              canFilter
              canExport
              canEdit={false}
              canAdd={false}
              csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Packages_Export`}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
              Add a new Package
            </Button>
          </Space>
        </Col>
      </Row>

      {showFilters && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col>
            <Input
              placeholder="Search..."
              value={filterHelper.getFieldValue(filters, 'name', '')}
              onChange={(e) => handleFilterChange(e.target.value, 'name')}
              allowClear
              style={{ width: 180 }}
            />
          </Col>
          <Col>
            <Select
              mode="multiple"
              allowClear
              placeholder="Minimum Weeks"
              value={filterHelper.getFieldValue(filters, 'minimumWeeks' as keyof ProductKey)}
              onChange={(vals) => handleFilterChange(vals, 'minimumWeeks' as keyof ProductKey)}
              options={getUniqueMinimumWeeksFilter(packageList)}
              style={{ width: 160 }}
            />
          </Col>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <FilterResetLink onClick={handleClearFilters} />
          </Col>
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={pagedData}
        rowKey="id"
        pagination={false}
        bordered
        size="middle"
        loading={loadingPackage}
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
      />

      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(value, page) => {
          setItemsPerPage(value);
          setCurrentPage(page);
        }}
        onPageChange={page => setCurrentPage(page)}
        showItemsPerPage
      />

      <Modal
        title={modalPackageId ? 'Edit Package' : 'Add Package'}
        open={modalOpen}
        onCancel={handleModalClose}
        footer={null}
        destroyOnHidden
        style={{ top: 48 }}
        width={980}
        className="add-edit-package-modal"
      >
        <AddEditPackageForm
          key={modalPackageId ?? 'new'}
          packageId={modalPackageId ?? undefined}
          onSuccess={handlePackageSaved}
        />
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  packageProducts: getPackageProductsData(state),
  filteredData: getFilteredData(state) as PackageProduct[] | undefined,
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  onFilterData: filteredData => dispatch(filterData(filteredData as unknown as Product[])),
  onPackagesData: packages => dispatch(listPackageProductsData(packages)),
  onSelectedPackageData: packageProduct => dispatch(getPackageProductById(packageProduct as PackageProduct)),
  onOpenNewPackage: () => {
    dispatch(setFormErrors([]));
    dispatch(resetPackageModal());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PackagesGrid);